import { Card } from "@/src/components/ui/card";
import { PointerInfoProps } from "../types/map.types";
import { Check, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/src/components/ui/button";
import { formatDateSmart } from "@/src/lib/date-utils";
import { useTranslations } from "next-intl";
import React, { useState } from "react";

/**
 * ScrollingBanner Component
 * Displays scrolling information in a continuous banner
 */
const ScrollingBanner = ({ items }: { items: string[] }) => {
  // Añadir separador al final para que el bucle sea continuo y seamless
  const scrollingContent = items.join(" • ") + " • ";

  return (
    <div className="w-full overflow-hidden bg-gradient-to-r from-green-600 to-green-700 text-white py-1.5 my-2 rounded shadow-sm">
      <div
        className="flex whitespace-nowrap"
        style={{
          animation: "banner-scroll 12s linear infinite",
        }}
      >
        {/* Dos copias idénticas para loop continuo sin salto */}
        <span className="text-xs font-medium">{scrollingContent}</span>
        <span className="text-xs font-medium">{scrollingContent}</span>
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes banner-scroll {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
        `,
        }}
      />
    </div>
  );
};

const PointerInfo = ({
  identifier,
  name,
  pointerType,
  status,
  inputsNames,
  additionalInfo,
  ugatDeviceInfo,
  providerServices,
  lastSync,
  metadata,
}: PointerInfoProps) => {
  // Estado para controlar la vista (device info o metadata)
  const [showDeviceInfo, setShowDeviceInfo] = useState(true);
  //console.log(ugatDeviceInfo);
  // Preparar datos para el banner
  const bannerItems: string[] = [];
  if (additionalInfo) {
    bannerItems.push(`Mode: ${additionalInfo.modeCurrent}`);
    if (additionalInfo.nextPlanSchedule) {
      // const nextPlanDate = new Date(additionalInfo.nextPlanSchedule);
      bannerItems.push(`Next Schedule: ${additionalInfo.nextPlanSchedule}`);
    }
    if (additionalInfo.isManualMode) {
      bannerItems.push("⚠️ Manual Mode Active");
    }
  }

  // Usar utilidad de fecha reutilizable
  const handleResolverLastSync = (lastSync: string) => {
    return formatDateSmart(lastSync, t("invalidDate"));
  };

  const t = useTranslations("dashboard.features.mapView.pointerDetails");

  return (
    <div className="flex flex-col w-full p-2">
      {/* <div className="border-t border-gray-300 my-2" /> */}

      {/* Información básica */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 w-full">
        {/* Informacion del Pointer */}
        <div className="flex flex-col space-y-2 justify-center">
          <div className="text-sm">
            <span className="font-semibold text-gray-900">
              {t("identifier")}:
            </span>
            <span className="ml-2 text-gray-700">{identifier}</span>
          </div>
          <div className="text-sm">
            <span className="font-semibold text-gray-900">{t("name")}:</span>
            <span className="ml-2 text-gray-700">{name}</span>
          </div>
          <div className="text-sm">
            <span className="font-semibold text-gray-900">{t("type")}:</span>
            <span className="ml-2 text-gray-700">{t(pointerType)}</span>
          </div>
          {pointerType === "Trafficlight" && additionalInfo && (
            <div className="text-sm">
              <span className="font-semibold text-gray-900">
                {t("providerDevices")}:
              </span>
              <span className="ml-2 text-gray-700">
                {additionalInfo.controllerProvider}(
                {additionalInfo.controllerModel})
              </span>
            </div>
          )}
          <div className="text-sm">
            <span className="font-semibold text-gray-900">{t("status")}:</span>
            <span className="ml-2 text-gray-700">{t(status)}</span>
          </div>
        </div>

        {/* Device Info and Metadata Section with Navigation */}
        {(ugatDeviceInfo || (metadata && Object.keys(metadata).length > 0)) && (
          <div className="flex flex-col space-y-2 relative">
            {/* Navigation Header */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-900">
                {showDeviceInfo ? t("deviceInfo") : t("metadata")}
              </span>

              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setShowDeviceInfo(!showDeviceInfo)}
                disabled={
                  !ugatDeviceInfo ||
                  !metadata ||
                  Object.keys(metadata).length === 0
                }
              >
                {showDeviceInfo ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Content Area */}
            <div className="min-h-[120px]">
              {showDeviceInfo && ugatDeviceInfo ? (
                <div className="flex flex-col space-y-1">
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">
                      {t("identifier")}:
                    </span>
                    <span className="ml-2 text-gray-700">
                      {ugatDeviceInfo.identifier}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">
                      {t("versionUgat")}:
                    </span>
                    <span className="ml-2 text-gray-700">
                      {ugatDeviceInfo.version}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">
                      {t("temperature")}:
                    </span>
                    <span className="ml-2 text-gray-700">
                      {ugatDeviceInfo.temperature}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">
                      {t("batteryLevel")}:
                    </span>
                    <span className="ml-2 text-gray-700">
                      {ugatDeviceInfo.voltage}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">
                      {t("operatorCompany")}:
                    </span>
                    <span className="ml-2 text-gray-700">
                      {ugatDeviceInfo.operator}
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">
                      {t("signalStrength")}:
                    </span>
                    <span className="ml-2 text-gray-700">
                      {ugatDeviceInfo.signalLevel}
                    </span>
                  </div>
                </div>
              ) : metadata && Object.keys(metadata).length > 0 ? (
                <div className="flex flex-col space-y-2">
                  {Object.entries(metadata).map(([key, value]) => (
                    <div key={key} className="text-sm">
                      <span className="font-semibold text-gray-900">
                        {key}:
                      </span>
                      <span className="ml-2 text-gray-700">{value}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-gray-500">
                  No additional information available
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Inputs y Traffic Info */}
      {ugatDeviceInfo && inputsNames && inputsNames.length > 0 && (
        <Card className="w-full p-4 mt-2 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Columna izquierda - Inputs */}
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-gray-900 mb-2 border-b pb-2">
                {t("ugatInputs")}
              </p>
              {inputsNames.map((input, index) => (
                <div
                  key={index}
                  className="flex flex-row items-center justify-between bg-white p-2 rounded shadow-sm"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {input.inputName === "Controller"
                      ? t("controllerInput")
                      : input.inputName === "BackDoors"
                        ? t("backDoorInput")
                        : input.inputName === "Doors"
                          ? t("doorInput")
                          : input.inputName === "Utc"
                            ? t("utcInputs")
                            : input.inputName}
                  </span>
                  {input.isActive ? (
                    <X className="h-5 w-5 text-red-600" />
                  ) : (
                    <Check className="h-5 w-5 text-green-600" />
                  )}
                </div>
              ))}
            </div>

            {/* Columna derecha - Traffic Info */}
            {additionalInfo && (
              <div className="flex flex-col items-center justify-center bg-white p-4 rounded shadow-sm">
                <p className="text-sm font-semibold text-gray-900 mb-3">
                  {t("trafficInfo")}
                </p>
                <div
                  className="relative flex justify-center w-16 h-16 items-center rounded-full border-4 my-2 bg-white shadow-md"
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    borderColor:
                      status === "Normal"
                        ? "#22c55e"
                        : status === "Warning"
                          ? "#f59e0b"
                          : status === "Information"
                            ? "#0ea5e9"
                            : "#ef4444",
                    color:
                      status === "Normal"
                        ? "#22c55e"
                        : status === "Warning"
                          ? "#f59e0b"
                          : status === "Information"
                            ? "#0ea5e9"
                            : "#ef4444",
                  }}
                >
                  {additionalInfo.planCurrent}
                </div>
                <div className="w-full flex flex-col items-center mt-2">
                  {/* Scrolling Banner */}
                  {bannerItems.length > 0 && (
                    <ScrollingBanner items={bannerItems} />
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Footer Provider and  LastSync */}
      <div className="flex flex-1 flex-row justify-between mt-4 text-sm text-gray-600 border-t pt-2">
        <p>
          <span className="font-semibold text-gray-900">
            {t("mantenanceProvider")}:
          </span>
          <span className="ml-2">{providerServices}</span>
        </p>
        <p>
          <span className="font-semibold text-gray-900">{t("lastSync")}:</span>
          <span className="ml-2">{handleResolverLastSync(lastSync)}</span>
        </p>
      </div>
    </div>
  );
};

export default PointerInfo;
